import React from "react";
import { ApiUser, AccessKey } from "@shared";
import { explisoftService, error as errorToast, success } from "@services";

export const useApiUsers = () => {
    const [apiUsers, setApiUsers] = React.useState<ApiUser[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        setLoading(true);
        explisoftService.appRegistrationGetApiUsers().then((data) => {
            setApiUsers(data);
        }).catch((error) => {
            errorToast(error.message || "Failed to fetch API users");
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const createUser = async (name: string): Promise<void> => {
        setLoading(true);
        explisoftService.appRegistrationCreateApiUser(name).then((data) => {
            setApiUsers((prev) => [...prev, data]);
            success("API User created successfully");
        }).catch((error) => {
            errorToast(error.message || "Failed to create API user");
        }).finally(() => {
            setLoading(false);
        });
    };

    const deleteUser = async (id: string): Promise<void> => {
        setLoading(true);
        explisoftService.appRegistrationDeleteApiUser(id).then(() => {
            setApiUsers((prev) => prev.filter((user) => user.id !== id));
            success("API User deleted successfully");
        }).catch((error) => {
            errorToast(error.message || "Failed to delete API user");
        }).finally(() => {
            setLoading(false);
        });
    };

    const rotateKey = async (userId: string, key: "1" | "2"): Promise<void> => {
        setLoading(true);
        explisoftService.appRegistrationRotateKey(userId, key).then((data: AccessKey) => {
            setApiUsers((prev) => prev.map((user) => {
                if (user.id === userId) {
                    return {
                        ...user,
                        accessKeys: user.accessKeys.map((accessKey) => {
                            if (accessKey.id === key) {
                                return { ...accessKey, key: data.key }
                            }
                            return accessKey;
                        })
                    }
                }
                return user;
            }));
            success("API Key rotated successfully");
        }).catch((error) => {
            errorToast(error.message || "Failed to rotate API key");
        }).finally(() => {
            setLoading(false);
        });
    };

    return {
        apiUsers,
        loading,
        createUser,
        deleteUser,
        rotateKey
    }
}

export const useApiUserScope = (apiUserId: string) => {
    const [scopes, setScopes] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        setLoading(true);
        explisoftService.appRegistrationGetApiUserScope(apiUserId).then((data) => {
            setScopes(data);
        }).catch((error) => {
            errorToast(error.message || "Failed to fetch API user scopes");
        }).finally(() => {
            setLoading(false);
        });
    }, [apiUserId]);

    const updateScope = React.useCallback((scope: string, addOrRemove: "add" | "remove") => {
        setLoading(true);
        explisoftService.appRegistrationUpdateApiUserScope(apiUserId, scope, addOrRemove).then(() => {
            setScopes((prev) => {
                if (addOrRemove === "add") {
                    return [...prev, scope];
                }
                return prev.filter((s) => s !== scope);
            });
            success("API User scope updated successfully");
        }).catch((error) => {
            errorToast(error.message || "Failed to update API user scope");
        }).finally(() => {
            setLoading(false);
        });
    }, [apiUserId]);

    return {
        scopes,
        loading,
        updateScope
    }
}